const SAFE_CODES = new Set([
  'mail_token_expired',
  'mail_scopes_missing',
  'mail_consent_required',
  'mail_admin_consent_required',
  'provider_rate_limited',
  'mail_provider_request_failed',
  'mail_action_outcome_unknown',
  'mail_integration_unavailable',
  'mail_job_payload_invalid',
]);

const TRUSTED_PROVIDERS = new Set(['google', 'microsoft']);
const RETRYABLE_CODES = new Set([
  'provider_rate_limited',
  'mail_provider_request_failed',
]);
const RECONNECT_CODES = new Set([
  'mail_token_expired',
  'mail_scopes_missing',
  'mail_consent_required',
]);
const SAFE_KEYS = Object.freeze(['code', 'provider', 'retryable']);
const GENERIC_SAFE_FAILURE = Object.freeze({
  code: 'mail_provider_request_failed',
  provider: null,
  retryable: false,
});

const freezePresentation = (presentation) => Object.freeze(presentation);

const GENERIC_FAILURE = freezePresentation({
  identity: 'generic:mail_provider_request_failed',
  title: 'We couldn’t complete this mailbox action.',
  detail: 'Try again.',
  actionLabel: 'Try again',
  busyLabel: 'Trying again…',
  recoveryIntent: 'retry',
  provider: null,
});

const MISSING_CONTEXT = freezePresentation({
  identity: 'generic:mailbox_context_unavailable',
  title: 'Mailbox context is unavailable.',
  detail: 'Mail actions are disabled until you sync this inbox again.',
  actionLabel: 'Try again',
  busyLabel: 'Trying again…',
  recoveryIntent: 'retry',
  provider: null,
});

const ACTION_OUTCOME_UNKNOWN = freezePresentation({
  identity: 'generic:mail_action_outcome_unknown',
  title: 'Some mailbox actions may already have completed.',
  detail: 'Review the affected messages before running triage again.',
  actionLabel: null,
  busyLabel: null,
  recoveryIntent: null,
  provider: null,
});

const PROVIDER_COPY = Object.freeze({
  google: Object.freeze({
    displayName: 'Gmail',
    reconnectIntent: 'connect_google',
  }),
  microsoft: Object.freeze({
    displayName: 'Outlook',
    reconnectIntent: 'connect_microsoft',
  }),
});

const hasExactSafeShape = (candidate) => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return false;

  const keys = Object.keys(candidate).sort();
  const expectedKeys = [...SAFE_KEYS].sort();
  return keys.length === expectedKeys.length
    && keys.every((key, index) => key === expectedKeys[index]);
};

const isValidSafeFailure = (candidate) => (
  hasExactSafeShape(candidate)
  && SAFE_CODES.has(candidate.code)
  && (candidate.provider === null || TRUSTED_PROVIDERS.has(candidate.provider))
  && typeof candidate.retryable === 'boolean'
  && (!candidate.retryable || RETRYABLE_CODES.has(candidate.code))
);

export const pickSafeMailFailureFields = (candidate) => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
  return {
    code: candidate.code ?? null,
    provider: candidate.provider ?? null,
    retryable: candidate.retryable ?? null,
  };
};

export const normalizeSafeMailFailure = (candidate) => {
  if (!isValidSafeFailure(candidate)) return GENERIC_SAFE_FAILURE;
  return {
    code: candidate.code,
    provider: candidate.provider,
    retryable: candidate.retryable,
  };
};

const reconnectPresentation = (candidate) => {
  const providerCopy = PROVIDER_COPY[candidate.provider];
  return freezePresentation({
    identity: `${candidate.provider}:${candidate.code}`,
    title: `${providerCopy.displayName} needs to be reconnected.`,
    detail: `Reconnect ${providerCopy.displayName}, then try again.`,
    actionLabel: `Reconnect ${providerCopy.displayName}`,
    busyLabel: `Reconnecting ${providerCopy.displayName}…`,
    recoveryIntent: providerCopy.reconnectIntent,
    provider: candidate.provider,
  });
};

const unavailablePresentation = (candidate) => {
  const providerCopy = PROVIDER_COPY[candidate.provider];
  return freezePresentation({
    identity: `${candidate.provider}:${candidate.code}`,
    title: `Your ${providerCopy.displayName} mailbox is no longer connected.`,
    detail: `Reconnect ${providerCopy.displayName} to continue.`,
    actionLabel: `Reconnect ${providerCopy.displayName}`,
    busyLabel: `Reconnecting ${providerCopy.displayName}…`,
    recoveryIntent: providerCopy.reconnectIntent,
    provider: candidate.provider,
  });
};

const rateLimitPresentation = (candidate) => {
  const providerCopy = PROVIDER_COPY[candidate.provider];
  return freezePresentation({
    identity: `${candidate.provider}:${candidate.code}`,
    title: `${providerCopy.displayName} is temporarily busy.`,
    detail: 'Try again in a moment.',
    actionLabel: 'Try again',
    busyLabel: 'Trying again…',
    recoveryIntent: 'retry',
    provider: candidate.provider,
  });
};

const microsoftAdminConsentPresentation = () => freezePresentation({
  identity: 'microsoft:mail_admin_consent_required',
  title: 'Outlook access needs administrator approval.',
  detail: 'Ask your Microsoft 365 administrator to approve mailbox access, then try Outlook again.',
  actionLabel: 'Try Outlook again',
  busyLabel: 'Trying Outlook again…',
  recoveryIntent: 'retry_microsoft_connection',
  provider: 'microsoft',
});

export const presentMailFailure = (candidate, options = {}) => {
  if (candidate === null || candidate === undefined) return MISSING_CONTEXT;
  if (!isValidSafeFailure(candidate)) return GENERIC_FAILURE;

  // Ambiguous provider mutations must never regain a replay action because
  // provider context is absent or stale. The closed code alone is sufficient
  // to choose the safer inspection-only state.
  if (candidate.code === 'mail_action_outcome_unknown') {
    return ACTION_OUTCOME_UNKNOWN;
  }

  if (Object.hasOwn(options, 'expectedProvider')) {
    if (
      !TRUSTED_PROVIDERS.has(options.expectedProvider)
      || candidate.provider !== options.expectedProvider
    ) {
      return GENERIC_FAILURE;
    }
  }

  if (candidate.code === 'mail_job_payload_invalid'
    || candidate.code === 'mail_provider_request_failed') {
    return GENERIC_FAILURE;
  }
  if (!TRUSTED_PROVIDERS.has(candidate.provider)) return GENERIC_FAILURE;
  if (RECONNECT_CODES.has(candidate.code)) return reconnectPresentation(candidate);
  if (candidate.code === 'mail_integration_unavailable') {
    return unavailablePresentation(candidate);
  }
  if (candidate.code === 'provider_rate_limited') return rateLimitPresentation(candidate);
  if (
    candidate.code === 'mail_admin_consent_required'
    && candidate.provider === 'microsoft'
  ) {
    return microsoftAdminConsentPresentation();
  }

  return GENERIC_FAILURE;
};

export const presentMailFailures = (candidates, options = {}) => {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return presentMailFailure(null);
  }

  const normalized = candidates.map(normalizeSafeMailFailure);
  if (normalized.some(({ code }) => code === 'mail_action_outcome_unknown')) {
    return ACTION_OUTCOME_UNKNOWN;
  }
  const [first] = normalized;
  const unanimous = normalized.every((candidate) => (
    candidate.code === first.code
    && candidate.provider === first.provider
    && candidate.retryable === first.retryable
  ));

  return unanimous
    ? presentMailFailure(first, options)
    : GENERIC_FAILURE;
};
